import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { axios } from 'app-zephyr-axios';
import {
  GET_ORG_NAVIGATION_ITEMS,
  post_pin_navigation_item,
  post_unpin_navigation_item,
  NavigationItemsType,
  get_org_navigation_items,
  NavigationItem,
  GET_PROJ_NAVIGATION_ITEMS,
  get_proj_navigation_items,
  GET_APP_NAVIGATION_ITEMS,
  get_app_navigation_items,
  NAVIGATION_QUERY_KEY_FIRST_ITEM,
} from 'ze-api-contract/user-v2/navigation-items';
import { GetOrgProjectListQueryParams } from 'ze-api-contract/organization-v2/get-project-list';

import { userPinsQuery } from './user-pins';
import { orgProjectList } from '../organization/organization-project-list';

export const navigationItemsQuery = createQueryKeys(NAVIGATION_QUERY_KEY_FIRST_ITEM, {
  organizationList: (queryParams?: { name?: string }) => ({
    queryKey: [GET_ORG_NAVIGATION_ITEMS, JSON.stringify(queryParams)],
    queryFn: async () => {
      return axios
        .get<{ entities: NavigationItem[] }>(get_org_navigation_items(), { params: queryParams })
        .then((res) => res.data.entities);
    },
  }),
  projectList: (organization: string, queryParams?: { name?: string }) => ({
    queryKey: [GET_PROJ_NAVIGATION_ITEMS, organization, JSON.stringify(queryParams)],
    queryFn: async () => {
      if (!organization) {
        return null;
      }

      return axios
        .get<{ entities: NavigationItem[] }>(get_proj_navigation_items({ organization }), { params: queryParams })
        .then((res) => res.data.entities);
    },
    enabled: Boolean(organization) as never,
  }),
  applicationList: (organization: string, project: string, queryParams?: { name?: string }) => ({
    queryKey: [GET_APP_NAVIGATION_ITEMS, organization, project, JSON.stringify(queryParams)],
    queryFn: async () => {
      if (!organization || !project) {
        return null;
      }

      return axios
        .get<{
          entities: NavigationItem[];
        }>(get_app_navigation_items({ organization, project }), { params: queryParams })
        .then((res) => res.data.entities);
    },
    enabled: (Boolean(organization) && Boolean(project)) as never,
  }),
});

export const useNavigationOrgItemList = (queryParams?: { name?: string }) => {
  const { data: organizationList, isLoading, error } = useQuery(navigationItemsQuery.organizationList(queryParams));

  return { organizationList, isLoading, error };
};

export const useNavigationProjItemList = (organization: string, queryParams?: { name?: string }) => {
  const { data: projectList, isLoading, error } = useQuery(navigationItemsQuery.projectList(organization, queryParams));

  return { projectList, isLoading, error };
};

export const useNavigationAppItemList = (organization: string, project: string, queryParams?: { name?: string }) => {
  const {
    data: applicationList,
    isLoading,
    error,
  } = useQuery(navigationItemsQuery.applicationList(organization, project, queryParams));

  return { applicationList, isLoading, error };
};

interface PropsItem {
  name?: string;
  id?: string;
  queryNameParam?: string;
}

export interface PinItemProps {
  type: NavigationItemsType;
  organization?: PropsItem;
  project?: PropsItem;
  application?: PropsItem;
}

export function usePinItem(projectListParams?: GetOrgProjectListQueryParams) {
  const queryClient = useQueryClient();
  const { queryKey: breadcrumbsQueryKey } = userPinsQuery.myPinList;

  return useMutation({
    mutationFn: async (value: PinItemProps): Promise<PinItemProps> => {
      const { type } = value;
      const reqBody = {
        type,
        id: value[type]?.id,
      };
      await axios.post(post_pin_navigation_item(), reqBody);
      return value;
    },
    onMutate: async (value: PinItemProps) => {
      const queryOrganizationKey = navigationItemsQuery.organizationList({ name: value.organization?.queryNameParam });
      const queryProjectKey = navigationItemsQuery.projectList(value.organization?.name ?? '', {
        name: value.project?.queryNameParam,
      });
      const queryAppKey = navigationItemsQuery.applicationList(
        value.organization?.name ?? '',
        value.project?.name ?? '',
        { name: value.application?.queryNameParam },
      );
      const queryKey =
        value.type === 'organization'
          ? queryOrganizationKey.queryKey
          : value.type === 'project'
            ? queryProjectKey.queryKey
            : queryAppKey.queryKey;
      await queryClient.cancelQueries({ queryKey });
      const navigationItems = queryClient.getQueryData<NavigationItem[]>(queryKey);
      const updatedNavigationItems = navigationItems?.map((item) => {
        if (item.id === value[value.type]?.id) {
          return { ...item, isPinned: true };
        }
        return item;
      });

      queryClient.setQueryData(queryKey, updatedNavigationItems);
    },
    onSettled: async (data: PinItemProps | undefined) => {
      if (!data) return;
      const queryOrganizationKey = navigationItemsQuery.organizationList({ name: data.organization?.queryNameParam });
      const queryProjectKey = navigationItemsQuery.projectList(data.organization?.name ?? '', {
        name: data.project?.queryNameParam,
      });
      const queryAppKey = navigationItemsQuery.applicationList(
        data.organization?.name ?? '',
        data.project?.name ?? '',
        { name: data.application?.queryNameParam },
      );
      const queryKey =
        data.type === 'organization'
          ? queryOrganizationKey.queryKey
          : data.type === 'project'
            ? queryProjectKey.queryKey
            : queryAppKey.queryKey;

      const { queryKey: projectListQueryKey } = orgProjectList.projectList({
        organization: data.organization?.name,
        queryParams: projectListParams ?? {},
      });

      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: projectListQueryKey });
      await queryClient.invalidateQueries({ queryKey: breadcrumbsQueryKey });
    },
  });
}

export function useUnPinItem(projectListParams?: GetOrgProjectListQueryParams) {
  const queryClient = useQueryClient();
  const { queryKey: breadcrumbsQueryKey } = userPinsQuery.myPinList;

  return useMutation({
    mutationFn: async (value: PinItemProps): Promise<PinItemProps> => {
      const { type } = value;
      const reqBody = {
        type,
        id: value[type]?.id,
      };
      await axios.post(post_unpin_navigation_item(), reqBody);
      return value;
    },
    onMutate: async (value: PinItemProps) => {
      const queryOrganizationKey = navigationItemsQuery.organizationList({ name: value.organization?.queryNameParam });
      const queryProjectKey = navigationItemsQuery.projectList(value.organization?.name ?? '', {
        name: value.project?.queryNameParam,
      });
      const queryAppKey = navigationItemsQuery.applicationList(
        value.organization?.name ?? '',
        value.project?.name ?? '',
        { name: value.application?.queryNameParam },
      );
      const queryKey =
        value.type === 'organization'
          ? queryOrganizationKey.queryKey
          : value.type === 'project'
            ? queryProjectKey.queryKey
            : queryAppKey.queryKey;
      await queryClient.cancelQueries({ queryKey });
      const navigationItems = queryClient.getQueryData<NavigationItem[]>(queryKey);
      const updatedNavigationItems = navigationItems?.map((item) => {
        if (item.id === value[value.type]?.id) {
          return { ...item, isPinned: false };
        }
        return item;
      });

      queryClient.setQueryData(queryKey, updatedNavigationItems);
    },
    onSettled: async (data: PinItemProps | undefined) => {
      if (!data) return;
      const queryOrganizationKey = navigationItemsQuery.organizationList({ name: data.organization?.queryNameParam });
      const queryProjectKey = navigationItemsQuery.projectList(data.organization?.name ?? '', {
        name: data.project?.queryNameParam,
      });
      const queryAppKey = navigationItemsQuery.applicationList(
        data.organization?.name ?? '',
        data.project?.name ?? '',
        { name: data.application?.queryNameParam },
      );
      const queryKey =
        data.type === 'organization'
          ? queryOrganizationKey.queryKey
          : data.type === 'project'
            ? queryProjectKey.queryKey
            : queryAppKey.queryKey;
      const { queryKey: projectListQueryKey } = orgProjectList.projectList({
        organization: data.organization?.name,
        queryParams: projectListParams ?? {},
      });

      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({ queryKey: projectListQueryKey });
      await queryClient.invalidateQueries({ queryKey: breadcrumbsQueryKey });
    },
  });
}
